import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api'; 
import EditParticipantModal from '../components/EditParticipantModal'; 
import BatchParticipantModal from '../components/BatchParticipantModal'; 
import { useAuth } from '../contexts/AuthContext'; 

// ⬇️ [모바일 고정 해제 1/3] baseStyles.filtersContainer.position을 'relative'로 변경
const baseStyles = {
  filtersContainer: { 
    background: '#f8f9fa', 
    padding: '15px', 
    borderRadius: '5px',
    position: 'relative', // ⬅️ 모바일 기본값 (고정 해제)
    top: 0, 
    zIndex: 10,
    borderBottom: '1px solid #dee2e6'
  },
  filterGroup: { display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' },
  filterItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  filterLabel: {
    fontSize: '0.8rem',
    fontWeight: 'bold',
    color: '#555',
    paddingLeft: '2px',
  },
  searchInput: { padding: '8px', fontSize: '0.9rem', width: '160px' }, 
  tableContainer: { marginTop: '20px', overflowX: 'auto' }, 
  table: { width: '100%', minWidth: '800px', borderCollapse: 'collapse' },
  th: { border: '1px solid #ddd', padding: '8px', background: '#f4f4f4', cursor: 'pointer', textAlign: 'center' },
  td: { border: '1px solid #ddd', padding: '8px', fontSize: '0.9rem', textAlign: 'center' },
  thCheckbox: { width: '40px', padding: '8px', background: '#f4f4f4', border: '1px solid #ddd', textAlign: 'center', cursor: 'default' },
  tdCheckbox: { width: '40px', padding: '8px', border: '1px solid #ddd', textAlign: 'center' },
  button: { padding: '5px 10px', cursor: 'pointer', border: 'none', borderRadius: '4px' },
  editButton: { backgroundColor: '#ffc107', color: 'black', whiteSpace: 'nowrap', fontSize: '0.85rem' },
  thManagement: { minWidth: '60px' },
  excelButton: { backgroundColor: '#28a745', color: 'white', padding: '8px 12px', marginRight: '10px' }, 
  batchButton: { backgroundColor: '#007bff', color: 'white', padding: '8px 12px', float: 'right' },
  deleteButton: { backgroundColor: '#dc3545', color: 'white', padding: '8px 12px', marginLeft: '10px' },
  message: { marginTop: '15px' },
  countDisplay: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#333',
    marginTop: '15px', 
    paddingLeft: '5px' 
  }
};


const ParticipantManagePage = () => {
  const { user } = useAuth(); 

  // ( ... state 선언 ... - 변경 없음)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [searchTerms, setSearchTerms] = useState({
    searchName: '',
    searchStatus: 'active',
    searchDistrict: '',
    searchPhone: '',
    searchWorkplace: '',
    searchRegistrar: '',
  });
  // 실제 조회에 사용되는 검색 조건 (검색 버튼을 눌렀을 때만 반영)
  const [appliedSearch, setAppliedSearch] = useState({
    searchName: '',
    searchStatus: 'active',
    searchDistrict: '',
    searchPhone: '',
    searchWorkplace: '',
    searchRegistrar: '',
  });
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ sortBy: 'name', sortOrder: 'ASC' });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingParticipant, setEditingParticipant] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  
  // ⬇️ [모바일 고정 해제 2/3] windowWidth state 추가
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // ⬇️ [모바일 고정 해제] windowWidth useEffect 추가
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ⬇️ [모바일 고정 해제 3/3] 동적 styles 변수 (position 속성 추가)
  const styles = {
    ...baseStyles,
    filtersContainer: {
      ...baseStyles.filtersContainer,
      // 1024px 초과 (데스크탑)일 때만 'sticky'로 변경하고 top: 60px
      position: windowWidth > 1024 ? 'sticky' : 'relative',
      top: windowWidth > 1024 ? '60px' : '0px', 
    }
  };


  // ( ... fetchProjects ... - 변경 없음)
  const fetchProjects = useCallback(async () => {
    if (!selectedYear) return;
    try {
      const res = await api.get(`/projects?year=${selectedYear}`);
      setProjects(res.data);
      setSelectedProject(''); 
    } catch (err) {
      console.error("사업 목록 로딩 실패", err);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // ( ... fetchParticipants ... - 변경 없음)
  const fetchParticipants = useCallback(async () => {
    setLoading(true); 
    setError(''); 
    setSelectedIds(new Set()); 
    const params = new URLSearchParams({
      year: selectedYear,
      ...appliedSearch, 
      ...sortConfig,
    });
    if (selectedProject) { 
      params.append('projectId', selectedProject); 
    }
    try {
      const res = await api.get(`/participants?${params.toString()}`);
      setParticipants(res.data); 
    } catch (err) {
      setError(err.response?.data?.message || '참여자 목록 로딩 실패'); 
    } finally {
      setLoading(false); 
    }
  }, [selectedYear, selectedProject, appliedSearch, sortConfig]);

  // ( ... useEffect (데이터 로드) ... - 변경 없음)
  useEffect(() => {
    if (selectedYear && hasSearched) {
      fetchParticipants();
    }
  }, [fetchParticipants, selectedYear, hasSearched]); 

  // ( ... handleFilterChange, handleSearchChange ... - 변경 없음)
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'selectedYear') setSelectedYear(parseInt(value, 10));
    if (name === 'selectedProject') setSelectedProject(value); 
  };
  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchTerms(prev => ({ ...prev, [name]: value })); 
  };

  // 검색 버튼 클릭 시에만 실제 검색 조건을 반영
  const handleSearchClick = () => {
    setAppliedSearch(searchTerms);
    setHasSearched(true);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchClick();
    }
  };
  
  // ( ... handleSort ... - 변경 없음)
  const handleSort = (columnName) => {
    let sortOrder = 'ASC';
    if (sortConfig.sortBy === columnName && sortConfig.sortOrder === 'ASC') { 
      sortOrder = 'DESC';
    }
    setSortConfig({ sortBy: columnName, sortOrder }); 
  };
  
  // ( ... handleEditClick ... - 변경 없음)
  const handleEditClick = (participant) => {
    setEditingParticipant(participant);
    setIsEditModalOpen(true); 
    setMessage(''); 
    setError(''); 
  };

  // ( ... handleModalSave ... - 변경 없음)
  const handleModalSave = async (id, updatedData) => {
    try {
      const res = await api.put(`/participants/${id}`, updatedData);
      setMessage('참여자 정보가 성공적으로 수정되었습니다.'); 
      setIsEditModalOpen(false); 
      setEditingParticipant(null);
      fetchParticipants(); 
    } catch (err) {
      let errorMessage = '수정 실패: ' + (err.response?.data?.message || err.message);
      if (err.response && err.response.status === 409) {
        errorMessage = err.response.data.message; 
        fetchParticipants();
      }
      alert(errorMessage);
    }
  };

  // ( ... handleExcelExport ... - 변경 없음)
  const handleExcelExport = async () => {
    const params = new URLSearchParams({
      year: selectedYear, ...searchTerms, ...sortConfig 
    });
    if (selectedProject) params.append('projectId', selectedProject); 
    try {
      const res = await api.get(`/participants/export?${params.toString()}`, {
        responseType: 'blob', 
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `participants_${selectedYear}.xlsx`; 
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('엑셀 다운로드 실패'); 
    }
  };

  // ( ... handleSelectOne, handleSelectAll, handleBatchSave ... - 변경 없음)
  const handleSelectOne = (participantId) => {
    const newSelectedIds = new Set(selectedIds); 
    if (newSelectedIds.has(participantId)) {
      newSelectedIds.delete(participantId);
    } else {
      newSelectedIds.add(participantId);
    }
    setSelectedIds(newSelectedIds); 
  };
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = participants.map(p => p.id); 
      setSelectedIds(new Set(allIds)); 
    } else {
      setSelectedIds(new Set()); 
    }
  };
  const handleBatchSave = async (batchData) => {
    const idsToUpdate = Array.from(selectedIds); 
    if (idsToUpdate.length === 0) {
      alert('선택된 항목이 없습니다.');
      return;
    }
    setLoading(true); 
    try {
      await api.put('/participants/batch', {
        ids: idsToUpdate,
        data: batchData
      });
      setMessage(`${idsToUpdate.length}개 항목이 일괄 변경되었습니다.`); 
      fetchParticipants(); 
    } catch (err) {
      setError(err.response?.data?.message || '일괄 변경 실패'); 
    } finally {
      setLoading(false); 
      setIsBatchModalOpen(false); 
    }
  };

  // 선택 삭제 핸들러
  const handleBatchDelete = async () => {
    const idsToDelete = Array.from(selectedIds);
    if (idsToDelete.length === 0) {
      alert('삭제할 항목을 선택하세요.');
      return;
    }

    // 선택된 참여자 이름 가져오기
    const selectedNames = participants
      .filter(p => idsToDelete.includes(p.id))
      .map(p => p.name)
      .join(', ');

    // 경고창 표시
    const confirmMessage = `선택한 ${idsToDelete.length}명의 참여자를 삭제하시겠습니까?\n\n삭제 대상: ${selectedNames}\n\n이 작업은 되돌릴 수 없습니다.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    try {
      const res = await api.delete('/participants/batch', {
        data: { ids: idsToDelete }
      });
      setMessage(res.data.message || `${idsToDelete.length}명의 참여자가 삭제되었습니다.`);
      setError('');
      setSelectedIds(new Set());
      fetchParticipants(); // 목록 새로고침
    } catch (err) {
      const data = err.response?.data;
      const blocked = data?.blocked;
      const baseMsg = data?.message || '삭제에 실패했습니다.';
      if (Array.isArray(blocked) && blocked.length > 0) {
        const lines = blocked.map(
          (b) =>
            `· ${b.name || '(이름 없음)'} (ID ${b.id}, 근무기록 ${b.workRecordCount ?? 0}건)`
        );
        const detail = [baseMsg, '', '— 근무기록이 있는 참여자 —', ...lines].join('\n');
        alert(detail);
        setError(detail);
      } else {
        setError(baseMsg);
      }
      setMessage('');
    } finally {
      setLoading(false);
    }
  };

  // ( ... 렌더링용 변수 ... - 변경 없음)
  const isAllSelected = participants.length > 0 && selectedIds.size === participants.length; 

  return (
    <div>
      <h2>참여자 관리</h2>
      
      {/* 1. 필터 및 검색 영역 (변경 없음) */}
      <div style={styles.filtersContainer}>
        <div style={styles.filterGroup}>
          
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>연도</span>
            <input
              type="number"
              name="selectedYear"
              style={styles.searchInput}
              value={selectedYear}
              onChange={handleFilterChange}
              placeholder="연도 입력 (예: 2024)"
              min="2020"
              step="1"
            />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>사업명</span>
            <select name="selectedProject" value={selectedProject} onChange={handleFilterChange} style={styles.searchInput}> 
              <option value="">-- 전체 사업명 --</option>
              {projects.map(p => (<option key={p.id} value={p.id}>{p.projectName}</option>))}
            </select>
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>상태</span>
            <select name="searchStatus" value={searchTerms.searchStatus} onChange={handleSearchChange} style={styles.searchInput}> 
              <option value="">-- 전체 상태 --</option>
              <option value="active">참여중</option>
              <option value="ended">종료</option>
            </select>
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>성명</span>
            <input type="text" name="searchName" placeholder="성명 검색" value={searchTerms.searchName} onChange={handleSearchChange} onKeyPress={handleSearchKeyPress} style={styles.searchInput} /> 
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>읍면동</span>
            <input type="text" name="searchDistrict" placeholder="읍면동 검색" value={searchTerms.searchDistrict} onChange={handleSearchChange} onKeyPress={handleSearchKeyPress} style={styles.searchInput} />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>연락처</span>
            <input
              type="text"
              name="searchPhone"
              placeholder="연락처(일부) 검색"
              value={searchTerms.searchPhone}
              onChange={handleSearchChange}
              onKeyPress={handleSearchKeyPress}
              style={styles.searchInput}
            />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>근무장소</span>
            <input type="text" name="searchWorkplace" placeholder="근무장소 검색" value={searchTerms.searchWorkplace} onChange={handleSearchChange} onKeyPress={handleSearchKeyPress} style={styles.searchInput} /> 
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>담당자</span>
            <input type="text" name="searchRegistrar" placeholder="담당자 검색" value={searchTerms.searchRegistrar} onChange={handleSearchChange} onKeyPress={handleSearchKeyPress} style={styles.searchInput} /> 
          </div>

          <div style={{ ...styles.filterItem, marginLeft: 'auto', flexDirection: 'row', gap: '8px' }}>
            <button
              style={styles.searchButton}
              onClick={handleSearchClick}
              disabled={loading}
            >
              🔍 검색
            </button>
            <button 
              style={styles.excelButton} 
              onClick={handleExcelExport}
              disabled={loading || participants.length === 0} 
            >
              엑셀 출력
            </button>
            <button 
              style={styles.batchButton} 
              onClick={() => setIsBatchModalOpen(true)} 
              disabled={loading || selectedIds.size === 0} 
            >
              선택일괄변경 ({selectedIds.size}) 
            </button>
            {user?.role === 'admin' && (
              <button 
                style={styles.deleteButton} 
                onClick={handleBatchDelete} 
                disabled={loading || selectedIds.size === 0} 
              >
                선택삭제 ({selectedIds.size})
              </button>
            )}
          </div>
        </div>
        
        {/* 인원 표시 ( ... 변경 없음 ... ) */}
        <div style={styles.countDisplay}>
          {loading ? '검색 중...' : `검색된 인원: 총 ${participants.length} 명`}
        </div>

      </div>
      
      {message && <p style={{...styles.message, color: 'green'}}>{message}</p>} 
      {error && <p style={{...styles.message, color: 'red', whiteSpace: 'pre-wrap'}}>{error}</p>} 

      {/* 2. 데이터 테이블 ( ... 변경 없음 ... ) */}
      <div style={styles.tableContainer}>
        {loading ? <p>로딩 중...</p> : ( 
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.thCheckbox}>
                  <input type="checkbox" checked={isAllSelected} disabled={participants.length === 0} onChange={handleSelectAll} /> 
                </th>
                <th style={styles.th} onClick={() => handleSort('name')}>성명 ↕</th>
                <th style={styles.th}>주민번호(일부)</th>
                <th style={styles.th} onClick={() => handleSort('district')}>읍면동 ↕</th>
                <th style={styles.th} onClick={() => handleSort('startDate')}>시작일 ↕</th>
                <th style={styles.th} onClick={() => handleSort('endDate')}>종료일 ↕</th>
                <th style={styles.th}>사업명</th>
                <th style={styles.th}>세부프로그램</th>
                <th style={styles.th} onClick={() => handleSort('workplace')}>근무장소 ↕</th>
                <th style={styles.th}>연락처</th>
                <th style={styles.th} onClick={() => handleSort('status')}>상태 ↕</th>
                <th style={styles.th} onClick={() => handleSort('registrar')}>담당자 ↕</th>
                <th style={{ ...styles.th, ...styles.thManagement }}>관리</th>
              </tr>
            </thead>
            <tbody>
              {participants.length > 0 ? participants.map(p => { 
                const isChecked = selectedIds.has(p.id); 
                return (
                  <tr key={p.id}>
                    <td style={styles.tdCheckbox}>
                      <input type="checkbox" checked={isChecked} onChange={() => handleSelectOne(p.id)} />
                    </td>
                    <td style={styles.td}>{p.name}</td>
                    <td style={styles.td}>{p.rrn.substring(0, 8)}...</td>
                    <td style={styles.td}>{p.district}</td>
                    <td style={styles.td}>{p.startDate || '-'}</td>
                    <td style={styles.td}>{p.endDate || '-'}</td>
                    <td style={styles.td}>{p.Project?.projectName}</td>
                    <td style={styles.td}>{p.programDetail || ''}</td>
                    <td style={styles.td}>{p.workplace}</td>
                    <td style={styles.td}>{p.phone}</td>
                    <td style={styles.td}>{p.status === 'active' ? '참여중' : '종료'}</td>
                    <td style={styles.td}>{p.registrar}</td>
                    <td style={{ ...styles.td, ...styles.thManagement }}>
                      <button style={{...styles.button, ...styles.editButton}} onClick={() => handleEditClick(p)}>
                        수정
                      </button>
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan="13" style={styles.td}>검색된 참여자가 없습니다.</td> 
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* 3. (개별) 수정 모달 ( ... 변경 없음 ... ) */}
      <EditParticipantModal
        isOpen={isEditModalOpen} 
        onClose={() => setIsEditModalOpen(false)} 
        onSave={handleModalSave}
        participant={editingParticipant}
        projects={projects} 
      />

      {/* 4. (일괄) 변경 모달 ( ... 변경 없음 ... ) */}
      <BatchParticipantModal
        isOpen={isBatchModalOpen} 
        onClose={() => setIsBatchModalOpen(false)} 
        onSave={handleBatchSave}
        projects={projects} 
      />
    </div>
  );
};

export default ParticipantManagePage;